import colors from "@constants/colors"
import Link from "next/link"
import { useRouter } from "next/router"
import React from "react"
import { up } from "styled-breakpoints"
import styled, { css } from "styled-components"
import { MenuStates } from "./common"
import { pushAnalyticsEvent } from "@utils/tracking"

const Container = styled.div<{ stacked?: boolean }>`
  ${({ stacked }) => css`
    align-items: center;
    justify-content: flex-start;
    display: flex;
    width: auto;
    min-width: 0;
    flex: ${stacked ? "0 1 auto" : "1 1 auto"};
    height: auto;
    border-bottom: none;
    background-color: transparent;

    &.powersports {
      min-width: auto;
    }

    ${up("desktop")} {
      min-width: 400px;
    }
  `}
`

const ModuleToggle = styled.div<{ stacked?: boolean }>`
  ${({ stacked }) => css`
    display: flex;
    align-items: center;
    margin-right: ${stacked ? "0" : "20px"};
    margin-left: 0;
    flex-shrink: 0;

    ${up("desktop")} {
      margin-right: ${stacked ? "0" : "40px"};
    }

    span {
      margin: 0;
      padding-bottom: 5px;
      white-space: nowrap;
    }

    a {
      transition: 0.3s all ease;
      border-bottom: 2px solid transparent;
      color: #aaa;
      white-space: nowrap;

      &.active,
      &:hover {
        font-weight: 600;

        &.automotive {
          color: ${colors.red};
          border-color: ${colors.red};
        }

        &.powersports {
          color: ${colors.alt};
          border-color: ${colors.alt};
        }

        &.new-vehicle {
          color: ${colors.yellow};
          border-color: ${colors.yellow};
        }
      }
    }
  `}
`

const Content = styled.div<{ stacked?: boolean; centered?: boolean }>`
  ${({ stacked, centered }) => css`
    align-items: center;
    justify-content: ${centered ? "center" : "flex-start"};
    display: flex;
    height: ${stacked ? "auto" : "100%"};
    white-space: nowrap;
    width: auto;
  `}
`

const activeProps = css`
  color: ${colors.snow};
  border-bottom: 2px solid ${colors.snow};
  transition: 0.6s ease-out;
`
const Button = styled.div<{ active }>`
  ${({ active }) => css`
    font-family: "Jost", sans-serif;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 14px;
    align-items: center;
    display: flex;
    height: 100%;
    margin: 0 8px;
    padding: 0;
    outline: none;
    border-bottom: 2px solid ${colors.black};
    background: none;
    color: ${colors.snow};
    flex-shrink: 0;
    white-space: nowrap;

    ${up("desktop")} {
      font-size: 16px;
      margin: 0 10px;
    }

    ${active ? activeProps : ""}

    &:focus,
    &:active {
      outline: none;
    }

    &:hover {
      cursor: pointer;
    }
  `}
`

interface HeaderProps {
  menuState?: undefined | MenuStates
  onResetVehicleSelectorMenu?: () => void
  onClickVehicleSelectorMenu?: () => void
  onClickPSSeatsMenu?: () => void
  onClickProSeriesMenu?: () => void
  onClickPSArmorMenu?: () => void
  onClickMainMenu?: () => void
  module?: string
  view?: "all" | "toggle" | "actions"
  stacked?: boolean
  centered?: boolean
}

const Header: React.FC<HeaderProps> = ({
  menuState,
  onResetVehicleSelectorMenu,
  onClickVehicleSelectorMenu,
  onClickProSeriesMenu,
  onClickPSSeatsMenu,
  onClickPSArmorMenu,
  onClickMainMenu,
  module = "automotive",
  view = "all",
  stacked = false,
  centered = false,
}) => {
  const { asPath } = useRouter()
  const isPowersports = asPath.includes("/powersports")
  const isLateModelPage = asPath === "/late-model"

  React.useEffect(() => {
    if (isPowersports) {
      pushAnalyticsEvent({
        event: "powersports_link_clicked",
      })
    }
  }, [isPowersports])

  return (
    <Container className={module} stacked={stacked}>
      <Content stacked={stacked} centered={centered}>
        {(view === "all" || view === "toggle") && (
          <ModuleToggle stacked={stacked}>
            <Button onClick={() => {}} active={false}>
              <Link
                className={
                  module == "automotive" && !isLateModelPage
                    ? "active automotive"
                    : "automotive"
                }
                href="/"
              >
                Restomod
              </Link>
            </Button>
            <span>/</span>
            <Button onClick={() => {}} active={false}>
              <Link
                className={
                  isLateModelPage ? "active new-vehicle" : "new-vehicle"
                }
                href="/late-model"
              >
                New Vehicle
              </Link>
            </Button>
            <span>/</span>
            <Button onClick={() => {}} active={false}>
              <Link
                className={
                  module == "powersports" ? "active powersports" : "powersports"
                }
                href="/powersports/"
              >
                Powersports
              </Link>
            </Button>
          </ModuleToggle>
        )}
        {(view === "all" || view === "actions") &&
          (module == "automotive" ? (
            <>
              <Button
                onClick={() => {
                  if (
                    onResetVehicleSelectorMenu &&
                    menuState === MenuStates.Picker
                  ) {
                    onResetVehicleSelectorMenu()
                  }
                  if (
                    onClickVehicleSelectorMenu &&
                    menuState !== MenuStates.Picker
                  ) {
                    onClickVehicleSelectorMenu()
                  }
                }}
                active={menuState && menuState === MenuStates.Picker}
              >
                Shop by Vehicle
              </Button>
              <Button
                onClick={() => {
                  if (onClickProSeriesMenu) {
                    onClickProSeriesMenu()
                  }
                }}
                active={menuState && menuState === MenuStates.ProSeries}
              >
                Seats
              </Button>
              <Button
                onClick={() => {
                  if (onClickMainMenu) {
                    onClickMainMenu()
                  }
                }}
                active={menuState && menuState === MenuStates.FullMenu}
              >
                Full Menu
              </Button>
              <Button onClick={() => {}} active={false}>
                <Link href={"/configurator"} prefetch={false}>
                  Configurator
                </Link>
              </Button>
              <Button onClick={() => {}} active={false}>
                <Link href={"/become-a-dealer"} prefetch={false}>
                  Become a Dealer
                </Link>
              </Button>
              {/* <Button onClick={() => {}} active={false}>
              <Link
                href={
                  (!module || module == "" || module == "automotive"
                    ? ""
                    : "/" + module) + "/upholstery"
                }
              >
                2020 models
              </Link>
            </Button> */}
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  if (
                    onResetVehicleSelectorMenu &&
                    menuState === MenuStates.Picker
                  ) {
                    onResetVehicleSelectorMenu()
                  }
                  if (
                    onClickVehicleSelectorMenu &&
                    menuState !== MenuStates.Picker
                  ) {
                    onClickVehicleSelectorMenu()
                  }
                }}
                active={menuState && menuState === MenuStates.Picker}
              >
                Shop by Vehicle
              </Button>
              <Button
                onClick={() => {
                  if (onClickPSSeatsMenu) {
                    onClickPSSeatsMenu()
                  }
                }}
                active={menuState && menuState === MenuStates.PSSeat}
              >
                Seats
              </Button>
              <Button onClick={() => {}} active={false}>
                <Link href={"/become-a-dealer"} prefetch={false}>
                  Become a Dealer
                </Link>
              </Button>
              {/* <Button
              onClick={() => {
                if (onClickPSArmorMenu) {
                  onClickPSArmorMenu()
                }
              }}
              active={menuState && menuState === MenuStates.PSArmor}
            >
              Exterior
            </Button> */}
            </>
          ))}
      </Content>
    </Container>
  )
}

export default Header
